<script lang="ts">
  import { transport } from "@/transport";
  import { categoryLabel, languageLabel } from "./categories";
  import type { FamilySummary } from "./types";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";
  import { AppBadge } from "@/components/ui";

  let {
    family,
    canFavorite = false,
    /** Whether this family is already loaded into the active Revit document. Computed by the parent
     *  (CatalogView) from a snapshot fetched once when the grid mounts — see catalogStore.loadProjectState
     *  and projectState.svelte.ts. Always false in the browser, where there is no project to check
     *  against. */
    inProject = false,
    /**
     * How much to show. The three differ in how much chrome each item carries, not in scale:
     * "cozy" is everything, "compact" drops the badge row and the category line, "list" lays the
     * same information out in a row so a narrow panel fits many more of them.
     */
    view = "cozy",
    onopen,
    onfavorite,
  }: {
    family: FamilySummary;
    canFavorite?: boolean;
    inProject?: boolean;
    view?: "cozy" | "compact" | "list";
    onopen?: (slug: string) => void;
    onfavorite?: (slug: string) => void;
  } = $props();

  const category = $derived(categoryLabel(family.revitCategory, locale.value as SupportedLocale));

  // Compatible with THIS Revit when a supported asset year is at or below the host's year. Outside
  // Revit (no host year) every family reads as available — the storefront does not gate on a version
  // it cannot know.
  const compatible = $derived.by(() => {
    const year = transport.caps.revitYear;
    if (year == null) return true;
    return family.availableYears.some((y) => y <= year);
  });

  type BadgeTone = "accent" | "success" | "warning" | "error" | "muted" | "neutral";

  // Where this family lives, when that is not the obvious answer. The public catalog needs no badge —
  // it is the default and every card would carry one — but an organization or private family looks
  // identical otherwise, and mistaking one for the other is how something internal gets treated as
  // published.
  const shelfBadge = $derived.by((): { text: string; tone: BadgeTone } | null => {
    if (family.visibility === "org") {
      return { text: family.organizationName || t("catalog.badgeOrg"), tone: "accent" };
    }
    if (family.visibility === "private") {
      return { text: t("catalog.badgePrivate"), tone: "muted" };
    }
    return null;
  });

  // Moderation state, shown only when it is not the settled one. A family sitting in the queue or
  // bounced back looks exactly like a live one on the "My families" shelf without this.
  const statusBadge = $derived.by((): { text: string; tone: BadgeTone } | null => {
    switch (family.status) {
      case "pending":
        return { text: t("catalog.badgePending"), tone: "warning" };
      case "rejected":
        return { text: t("catalog.badgeRejected"), tone: "error" };
      case "draft":
        return { text: t("catalog.badgeDraft"), tone: "muted" };
      default:
        return null;
    }
  });

  const isList = $derived(view === "list");
  const showDetail = $derived(view !== "compact");

  // A compact label for the covered Revit versions: a single year, or "2022–2024" for a range.
  const yearRange = $derived.by(() => {
    const years = [...family.availableYears].sort((a, b) => a - b);
    if (years.length === 0) return "—";
    if (years.length === 1) return `Revit ${years[0]}`;
    return `Revit ${years[0]}–${years[years.length - 1]}`;
  });

  function onFavoriteClick(event: MouseEvent) {
    event.stopPropagation();
    onfavorite?.(family.slug);
  }
</script>

<!-- The card is one big button, so the heart cannot live inside it — a button within a button is
     invalid and swallows the click. It is layered over the card instead, which also gives it the
     hover-reveal behaviour without a second hit target competing for the same pixels.

     List view is the one exception: there the card is a single-line row and the top-right corner
     the heart floats over in every other view is not empty — it is where the badges (authoring
     language, unit system, Revit years) already sit, so an absolutely-positioned heart lands right
     on top of that text instead of beside it. There, the heart takes a real slot in the row's own
     flex flow instead of floating, so it never has to share a pixel with anything else. -->
<div class="relative group {isList ? 'flex items-center gap-1' : ''}">
  <button
    class="text-left rounded-surface border border-line bg-white overflow-hidden hover:border-accent transition-colors {isList
      ? 'flex-1 min-w-0 flex items-center gap-3 p-2'
      : 'w-full h-full flex flex-col'}"
    onclick={() => onopen?.(family.slug)}
  >
    <!-- Thumbnail, or a placeholder tile until a preview is built. -->
    <!-- w-full is load-bearing on the grid variants, not decoration. The card is a <button>, and a
         button's UA style centres its flex children instead of stretching them, so the tile sized
         itself to its content: fine for a card with a thumbnail, and collapsed to a 40px square for
         one showing only the placeholder icon — which made rows of unpublished-preview families
         look ragged. Asking for the width outright is immune to whatever the UA decides. -->
    <div
      class="bg-panel flex items-center justify-center overflow-hidden shrink-0 {isList ? 'w-12 h-12 rounded' : 'w-full aspect-[4/3]'}"
    >
      <!-- Rendered at its native size (max-*, not w/h-full): the embedded Revit preview is small
           (~256px), so filling the tile would upscale it into a blurry, pixelated mess. Capping at
           the intrinsic size keeps it crisp and, in effect, about half the tile — centred by the
           flex parent. -->
      {#if family.thumbnailUrl}
        <img src={family.thumbnailUrl} alt={family.name} class="max-w-full max-h-full object-contain" />
      {:else}
        <svg
          viewBox="0 0 24 24"
          class="text-ink-muted {isList ? 'w-5 h-5' : 'w-10 h-10'}"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <path d="m3.3 7 8.7 5 8.7-5M12 22V12" />
        </svg>
      {/if}
    </div>

    <div class="min-w-0 flex-1 {isList ? 'flex items-center gap-3' : 'p-3 flex flex-col gap-2'}">
      <div class="flex-1 min-w-0">
        <p class="font-medium text-sm truncate" title={family.name}>{family.name}</p>
        {#if showDetail}
          <p class="text-xs text-ink-muted truncate">
            {category}
            <!-- The .rfa's own name, in the one view with room for it. People who have kept a library
                 in folders for years look for this before they look at anything else. -->
            {#if isList && family.sourceFileName}
              · {family.sourceFileName}
            {/if}
          </p>
        {/if}
      </div>

      <!-- Compact drops the badge row entirely. It is the row that costs the most vertical space
           per item and the least when someone is scanning a folder they already understand. -->
      {#if showDetail}
        <div class="flex items-center gap-1.5 flex-wrap {isList ? 'shrink-0' : ''}">
          <!-- A system type behaves differently once downloaded — it joins the project's types rather
               than the project browser, and there is no instance to place — so it is worth saying
               before the click, not after. -->
          {#if family.kind === "system"}
            <AppBadge tone="accent" title={t("catalog.badgeSystemHint")}>
              {t("catalog.badgeSystem")}
            </AppBadge>
          {/if}

          <!-- Already in the open project. Revit-only by construction: inProject is always false in
               the browser, where there is no project to check against. -->
          {#if inProject}
            <AppBadge tone="success" title={t("catalog.badgeInProjectHint")}>
              {t("catalog.badgeInProject")}
            </AppBadge>
          {/if}

          <!-- Shelf and review state first: they change what the card *is*, not just what it contains. -->
          {#if shelfBadge}
            <AppBadge tone={shelfBadge.tone} title={shelfBadge.text} class="truncate max-w-[10rem]">
              {shelfBadge.text}
            </AppBadge>
          {/if}
          {#if statusBadge}
            <AppBadge tone={statusBadge.tone}>{statusBadge.text}</AppBadge>
          {/if}

          <!-- Authoring-language badge: the family's parameters read in this language. -->
          {#if family.authoringLanguage}
            <AppBadge tone="neutral">{languageLabel(family.authoringLanguage)}</AppBadge>
          {/if}
          {#if family.unitSystem}
            <AppBadge tone="muted">{family.unitSystem}</AppBadge>
          {/if}
          {#if family.lod}
            <AppBadge tone="muted" title={t("publish.lodHint")}>LOD {family.lod}</AppBadge>
          {/if}
          <!-- Compatibility: inside Revit, red when no asset fits this host; neutral otherwise,
               showing the covered version range so the buyer knows what they get. -->
          <AppBadge tone={compatible ? "muted" : "error"}>{yearRange}</AppBadge>
        </div>
      {/if}
    </div>
  </button>

  <!-- Always rendered once marked, revealed on hover otherwise: a filled heart is state the user
       must be able to see at a glance, an empty one is an affordance that would only add noise to
       every tile in the grid. -->
  {#if canFavorite}
    <button
      class="{isList
        ? 'shrink-0'
        : 'absolute top-2 right-2'} p-1.5 rounded-full bg-white/90 backdrop-blur-sm transition-opacity focus:opacity-100 {family.isFavorite
        ? 'opacity-100 text-error'
        : 'opacity-0 group-hover:opacity-100 text-ink-muted hover:text-error'}"
      aria-label={family.isFavorite ? t("catalog.unfavorite") : t("catalog.favorite")}
      aria-pressed={family.isFavorite}
      title={family.isFavorite ? t("catalog.unfavorite") : t("catalog.favorite")}
      onclick={onFavoriteClick}
    >
      <svg
        viewBox="0 0 24 24"
        class="w-4 h-4"
        fill={family.isFavorite ? "currentColor" : "none"}
        stroke="currentColor"
        stroke-width="1.8"
      >
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.7 1.1-1a5.5 5.5 0 0 0 0-7.7z" />
      </svg>
    </button>
  {/if}
</div>
